import { calculateConvertedPrice } from '@monorepo/hub-common';
import {
  CartEntity,
  CartItemEntity,
  CartStep,
  CouponItemType,
  isMetadataFromType,
  OptionalAssets,
  ProductType,
  Program,
  ProgramMetadata,
  SupportedCurrencies,
} from '@monorepo/types';

export interface GtmEcommerceItem {
  item_id: string;
  item_name: string;
  price: number;
  quantity: number;
  group_name: string | undefined;
  trading_system: string;
}

export const buildGtmItemFromProgram = (
  program: Program,
): GtmEcommerceItem => ({
  item_id: program._id,
  item_name: program.name ?? 'unknown',
  price: program.price ?? 0,
  quantity: 1,
  group_name: program.groupName,
  trading_system: program.tradingSystem ?? 'na',
});

export const buildGtmItemFromCartItem = (
  item: CartItemEntity,
): GtmEcommerceItem => {
  const meta = item.metadata as ProgramMetadata | undefined;
  return {
    item_id: item.productId,
    item_name:
      item.productType === ProductType.Program
        ? (meta?.productName ?? 'unknown')
        : item.productType,
    price: item.unitPrice,
    quantity: item.quantity,
    group_name: meta?.groupName,
    trading_system: meta?.tradingSystem ?? 'na',
  };
};

interface ProgramMetadataResult {
  groupName: string;
  buyingPower: number;
  tradingSystem: string;
  totalAmountUsd: number;
  totalAmountLocal: number;
}

export const extractProgramMetadata = (
  cartData: CartEntity[OptionalAssets] | undefined,
  currency: SupportedCurrencies,
  currencyRates?: Record<string, number>,
): ProgramMetadataResult => {
  const items = cartData?.items || [];
  const programItem = items.find((item) =>
    isMetadataFromType(item, ProductType.Program),
  );

  const programMetadata = programItem ? programItem.metadata : null;

  const {
    groupName = 'na',
    buyingPower = 0,
    tradingSystem = 'na',
  } = programMetadata || {};

  const totalPriceUsd = cartData?.originalPrice || 0;
  const totalAmountLocal = calculateConvertedPrice(
    totalPriceUsd,
    currency,
    currencyRates,
  );

  return {
    groupName,
    buyingPower,
    tradingSystem,
    totalAmountUsd: totalPriceUsd,
    totalAmountLocal,
  };
};

export const getBaseMixpanelProperties = (
  cart: CartEntity | undefined,
  asset: OptionalAssets,
  currency: SupportedCurrencies,
  currencyRates?: Record<string, number>,
) => {
  const cartData = cart?.[asset];
  const metadata = extractProgramMetadata(cartData, currency, currencyRates);

  return {
    asset,
    group_name: metadata.groupName,
    buying_power: metadata.buyingPower,
    trading_system: metadata.tradingSystem,
    total_amount_usd: metadata.totalAmountUsd,
    total_amount_local: metadata.totalAmountLocal,
    currency,
  };
};

export const getBackClickStepProperties = (
  cart: CartEntity | undefined,
  currentStep: CartStep,
  currentAsset: OptionalAssets | undefined,
  currency: SupportedCurrencies,
  currencyRates?: Record<string, number>,
) => {
  const cartData = currentAsset ? cart?.[currentAsset] : undefined;
  const items = cartData?.items || [];
  const itemsCount = items.length;
  const cartEmpty = itemsCount === 0;

  const baseProperties = {
    cart_empty: cartEmpty,
    items_count: itemsCount,
  };

  if (cartEmpty) {
    switch (currentStep) {
      case CartStep.Billing:
      case CartStep.Programs:
        return {
          ...baseProperties,
          ...(currentAsset ? { asset: currentAsset } : {}),
        };
      case CartStep.Assets:
        return {
          ...baseProperties,
          ...(currentAsset ? { asset: currentAsset } : {}),
        };
      default:
        return baseProperties;
    }
  }

  const metadata = extractProgramMetadata(cartData, currency, currencyRates);

  switch (currentStep) {
    case CartStep.Billing:
      return {
        ...baseProperties,
        asset: currentAsset,
        group_name: metadata.groupName,
        buying_power: metadata.buyingPower,
        trading_system: metadata.tradingSystem,
        total_amount_local: metadata.totalAmountLocal,
        total_amount_usd: metadata.totalAmountUsd,
        currency,
      };
    case CartStep.Programs:
      return {
        ...baseProperties,
        asset: currentAsset,
        group_name: metadata.groupName,
        buying_power: metadata.buyingPower,
        trading_system: metadata.tradingSystem,
        total_amount_local: metadata.totalAmountLocal,
        total_amount_usd: metadata.totalAmountUsd,
        currency,
      };
    case CartStep.Assets:
      return {
        ...baseProperties,
        ...(currentAsset ? { asset: currentAsset } : {}),
        group_name: metadata.groupName,
      };
    default:
      return baseProperties;
  }
};

export const getDeleteCartTrackingProperties = (
  cart: CartEntity | undefined,
  currentAsset: OptionalAssets | undefined,
  currency: SupportedCurrencies,
  currencyRates?: Record<string, number>,
) => {
  const cartData = currentAsset ? cart?.[currentAsset] : undefined;
  const items = cartData?.items || [];
  const itemsCount = items.length;
  const metadata = extractProgramMetadata(cartData, currency, currencyRates);

  return {
    items_count: itemsCount,
    total_amount_local: metadata.totalAmountLocal,
    total_amount_usd: metadata.totalAmountUsd,
    currency,
    ...(currentAsset ? { asset: currentAsset } : {}),
    ...(metadata.groupName !== 'na' ? { group_name: metadata.groupName } : {}),
    ...(metadata.buyingPower > 0 ? { buying_power: metadata.buyingPower } : {}),
    ...(metadata.tradingSystem !== 'na'
      ? { trading_system: metadata.tradingSystem }
      : {}),
  };
};

export interface CouponTrackingProperties {
  is_coupon: boolean;
  coupon_code: string | null;
  coupon_discount: number | null;
  coupon_discount_type: string | null;
  coupon_discount_amount_usd: number | null;
}

export const getCouponTrackingProperties = (
  cart: CartEntity | undefined,
  asset: OptionalAssets,
): CouponTrackingProperties => {
  const cartData = cart?.[asset];
  const coupon = cartData?.discountMetadata?.couponDiscount;

  if (!coupon) {
    return {
      is_coupon: false,
      coupon_code: null,
      coupon_discount: null,
      coupon_discount_type: null,
      coupon_discount_amount_usd: null,
    };
  }

  const { discountType, amount } = coupon.discountMetadata;
  const originalPrice = cartData?.originalPrice || 0;

  const discountAmountUsd =
    discountType === CouponItemType.Percentage
      ? (originalPrice * amount) / 100
      : amount;

  return {
    is_coupon: true,
    coupon_code: coupon.couponName,
    coupon_discount: amount,
    coupon_discount_type: discountType,
    coupon_discount_amount_usd: Math.round(discountAmountUsd * 100) / 100,
  };
};
